import React, { useEffect, useRef, useState } from "react";
import * as Yup from 'yup';
import { Form, Formik, Field } from "formik";
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { Link, useNavigate } from "react-router-dom";
import { Bounce, toast, ToastContainer } from "react-toastify";

import './Form.css'
import { sendDataToApi } from "./FormUtils";
import FormTitle from "../../molecules/form-title/FormTitle";
import OauthLoginBtn from "../../molecules/oauth-login/OauthLoginBtn";

const LoginForm = () => {
    const navigate = useNavigate();
    const [isSponsor, setIsSponsor] = useState(true);
    const toggleUserType = () => setIsSponsor((prev) => !prev);
    const lastErrorsRef = useRef<{ email?: string; password?: string }>({});
    const [formikValues, setFormikValues] = useState({ email: '', password: '' });
    const [formikErrors, setFormikErrors] = useState<{ email?: string; password?: string }>({});
    const [touchedFields, setTouchedFields] = useState<{ email?: boolean; password?: boolean }>({});
    const [submitCount, setSubmitCount] = useState(0);
  
    const validationSchema = Yup.object({
      email: Yup.string()
        .email("Please enter a valid email")
        .required("Email is a required field"),
      password: Yup.string()
        .min(8, "Password should contain at least 8 characters")
        .required("Password is a required field"),
    }); 

    useEffect(() => {
        const errorKeys = submitCount > 0
            ? (Object.keys(formikErrors) as Array<keyof typeof formikErrors>)
            : (Object.keys(touchedFields) as Array<keyof typeof touchedFields>)
                .filter((field) => touchedFields[field] === true);

        errorKeys.forEach((field) => {
            if (formikErrors[field] && formikErrors[field] !== lastErrorsRef.current[field]) {
                toast.error(formikErrors[field] as string, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "dark",
                    transition: Bounce,
                });
                lastErrorsRef.current[field] = formikErrors[field] as string;
            }
        });
    }, [formikErrors, touchedFields, submitCount]);

    return (
        <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
                const loginApiUrl = import.meta.env.VITE_LOGIN_URL as string;
                sendDataToApi(loginApiUrl, "", values, navigate);
                setSubmitting(false);
            }}
            validate={(values) => {
                try {
                    validationSchema.validateSync(values, { abortEarly: false });
                    setFormikErrors({});
                } catch (error) {
                    if (error instanceof Yup.ValidationError) {
                        const newErrors: { [key: string]: string } = {};
                        error.inner.forEach((err) => {
                            newErrors[err.path as string] = err.message;
                        });
                        setFormikErrors(newErrors);
                    }
                }
                setFormikValues(values);
            }}
        >
            {(formik) => {
                setTouchedFields(formik.touched);
                setSubmitCount(formik.submitCount);

                return (
                    <Form className="formContainer" onSubmit={formik.handleSubmit}>
                        <ToastContainer />
                        <FormTitle title="Sign In" text="Get access to your account" />
                        {
                            isSponsor
                            ? <></>
                            :<OauthLoginBtn microsoft="Login with Microsoft" google="Login with Google" />
                        }
                        
                        <div className="formField">
                            <div className="hrContainer">
                                <hr className="leftHr" />
                                <small className="hrText">or use email</small>
                                <hr className="rightHr" />
                            </div>
                            <div className="toggle-container">
                                <span className="toggle-text">
                                    {isSponsor
                                        ? "You're logging in as an Sponsor"
                                        : "You're logging in as an Beneficiary"}
                                </span>
                                <label className="switch">
                                    <input type="checkbox" checked={!isSponsor} onChange={toggleUserType} />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                            <div className="fieldAndIcon">
                                <div className="icon">
                                    <PersonIcon />
                                </div>
                                <Field autoComplete="off" className="formInput" id="email" name="email" type="email" placeholder="Email" onChange={formik.handleChange} value={formik.values.email} />
                            </div>
                            
                            <div className="fieldAndIcon">
                                <div className="icon">
                                    <LockIcon />
                                </div>
                                <Field autoComplete="off" className="formInput" id="password" name="password" type="password" placeholder="Password" onChange={formik.handleChange} value={formik.values.password} />
                            </div>
                            
                            <div className="rememberMeContainer">
                                <div className="remeberMe">
                                    <input type="radio" name="rememberMe" id="rememberMe" />
                                    <label htmlFor="rememberMe">Remember Me</label>
                                </div>
                                <div className="forgotPwd">
                                    <Link to='/forgot-pwd'>
                                        <small className="forgotPassword">Forgot Password?</small>
                                    </Link>
                                </div>
                            </div>
                            <input className="submitBtn" type="submit" value="Sign In" />
                            <small className="formText">Don't have an account? <Link to="/register"><span>Create Account</span></Link></small>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default LoginForm;